import { htmlElements } from '../helpers/htmlElements'
import type { PaysafeError } from '../models/paysafe'
import type { AchFormData, InitOptions } from '../models/sdkModels'
import { initOptionsSchema } from '../models/sdkModels'
import { generatePaysafeToken } from './paysafe'
import type { CheckoutHandler } from './sdk'
import { dispatchEvent, CheckoutEvent } from './events'

export interface PaymentOptionState {
    achFormData?: AchFormData | null
    pspToken?: string
    formIsValid: boolean
}

type FieldError =
    | 'accountNumberError'
    | 'routingNumberError'
    | 'termsAndConditionsError'
    | 'cardNumberError'
    | 'expiryDateError'
    | 'CVVError'

export const validatePaymentOption = async (
    checkoutInstance: CheckoutHandler,
): Promise<PaymentOptionState> => {
    let achFormData: AchFormData | null = null
    let pspToken: string | undefined = undefined
    let formIsValid = true

    if (checkoutInstance.paymentOption === 'ach') {
        achFormData = validateACHForm()
        if (!achFormData) {
            formIsValid = false
        }
    } else if (checkoutInstance.paymentOption === 'card') {
        const paysafeTokenResponse =
            await generatePaysafeToken(checkoutInstance)

        if (
            !paysafeTokenResponse.fieldErrors &&
            paysafeTokenResponse.data?.token
        ) {
            pspToken = paysafeTokenResponse.data.token
        } else {
            formIsValid = false

            const errors: FieldError[] = []
            console.log(
                'paysafe field errors',
                JSON.stringify(paysafeTokenResponse.fieldErrors),
            )
            paysafeTokenResponse.fieldErrors?.forEach((error: PaysafeError) => {
                switch (error.field) {
                    case 'card number':
                        errors.push('cardNumberError')
                        break
                    case 'cvv':
                        errors.push('CVVError')
                        break
                    case 'expiry date':
                        errors.push('expiryDateError')
                        break
                }
            })
            displayErrorMessages(errors)
        }
    }

    return { achFormData, pspToken, formIsValid }
}

/**
 * Validates a US bank account number by checking if it matches the expected format.
 *
 * US bank account numbers can vary in length depending on the financial institution,
 * typically ranging from 8 to 17 digits. This function performs basic format validation
 * but does not guarantee the account number is actually valid or active.
 *
 * @param accountNumber - The bank account number to validate.
 *                     Can be undefined (e.g., when dealing with optional form fields).
 *                     Should contain only numeric characters.
 *
 * @returns {boolean} Returns true if the account number is:
 *                    - defined (not undefined)
 *                    - contains only digits
 *                    - has a length between 8 and 17 characters
 *                    Returns false otherwise.
 *
 * @example
 * // Valid account numbers
 * validAccountNumber("12345678")     // returns true
 * validAccountNumber("1234567890")   // returns true
 * validAccountNumber("12345678901")  // returns true
 *
 * // Invalid account numbers
 * validAccountNumber(undefined)      // returns false
 * validAccountNumber("123456")       // returns false (too short)
 * validAccountNumber("ABC123456")    // returns false (contains letters)
 * validAccountNumber("")             // returns false
 *
 * @note
 * - Different banks use different account number lengths
 * - Most common lengths are 10-12 digits
 * - Some banks use 8 or 9 digits
 * - A few banks might use up to 17 digits
 * - For production use, consider additional validation:
 *   - Bank-specific format rules
 *   - Check digit validation
 *   - Integration with bank verification APIs
 */
export const validAccountNumber = (
    accountNumber: string | undefined,
): boolean => {
    if (accountNumber) {
        return /^\d{8,17}$/.test(accountNumber)
    } else {
        return false
    }
}

/**
 * Validates a US bank routing number using both format and checksum verification.
 *
 * A valid routing number must:
 * 1. Be exactly 9 digits
 * 2. First two digits must be in ranges: 00-12, 21-32, 61-72, or 80
 * 3. Pass the checksum algorithm where sum of (digit * weight) is divisible by 10
 *
 * @param routingNumber - The routing number to validate
 * @returns True if the routing number is valid, false otherwise
 *
 * @example
 * ```typescript
 * validateRoutingNumber('123456789') // false
 * validateRoutingNumber('021000021') // true
 * validateRoutingNumber('invalid')   // false
 * ```
 */
export function validRoutingNumber(routingNumber: string | undefined): boolean {
    if (!routingNumber) return false

    // Check basic format using regex
    if (!/^\d{9}$/.test(routingNumber)) {
        return false
    }

    // Validate first two digits are in valid ranges
    const firstTwoDigits = parseInt(routingNumber.slice(0, 2), 10)
    const isValidPrefix =
        (firstTwoDigits >= 0 && firstTwoDigits <= 12) ||
        (firstTwoDigits >= 21 && firstTwoDigits <= 32) ||
        (firstTwoDigits >= 61 && firstTwoDigits <= 72) ||
        firstTwoDigits === 80

    if (!isValidPrefix) {
        return false
    }

    // Checksum weights for positions 1-9
    const weights = [3, 7, 1, 3, 7, 1, 3, 7, 1] as const

    // Calculate checksum
    const checksum = routingNumber
        .split('')
        .reduce(
            (sum, digit, index) => sum + parseInt(digit, 10) * weights[index],
            0,
        )

    // Valid if checksum is divisible by 10
    return checksum % 10 === 0
}

export const validateACHForm = (): AchFormData | null => {
    const { form, dgLoader, termCheckbox, accountNumber, routingNumber } =
        htmlElements()
    if (!form) {
        console.error('Form not found')
        return null
    }

    clearErrorMessages()

    const errors: FieldError[] = []

    if (
        !validAccountNumber(accountNumber) ||
        !validRoutingNumber(routingNumber) ||
        termCheckbox.checked === false
    ) {
        if (!validAccountNumber(accountNumber)) {
            errors.push('accountNumberError')
        }

        if (!validRoutingNumber(routingNumber)) {
            errors.push('routingNumberError')
        }

        if (termCheckbox.checked === false) {
            errors.push('termsAndConditionsError')
        }

        displayErrorMessages(errors)
        return null
    } else {
        // Valid form
        if (dgLoader) {
            dgLoader.style.display = 'flex'
        }

        const achFormData: AchFormData = {
            accountNumber: accountNumber,
            routingNumber: routingNumber,
        }

        return achFormData
    }
}

export const clearErrorMessages = (): void => {
    const htmlElementList = htmlElements()
    const errors: FieldError[] = [
        'accountNumberError',
        'routingNumberError',
        'termsAndConditionsError',
        'cardNumberError',
        'expiryDateError',
        'CVVError',
    ]
    errors.forEach((error: FieldError) => {
        const errorElement = htmlElementList[error]
        const previousElement: Element | null =
            errorElement?.previousElementSibling
        errorElement?.classList.add('gc-dg-hidden')
        errorElement?.removeAttribute('role')
        previousElement?.classList.remove('gc-dg-error')
        previousElement?.removeAttribute('aria-invalid')
        previousElement?.removeAttribute('aria-describedby')
    })
}

export const displayErrorMessages = (errors: FieldError[]): void => {
    const htmlElementList = htmlElements()
    htmlElementList.submitButton.blur()

    errors.forEach((error: FieldError) => {
        const errorElement = htmlElementList[error]
        const previousElement: Element | null =
            errorElement?.previousElementSibling
        errorElement?.classList.remove('gc-dg-hidden')
        previousElement?.classList.add('gc-dg-error') // Adds border colour style to input
        previousElement?.setAttribute('aria-invalid', 'true')
        previousElement?.setAttribute('aria-describedby', `gc-dg-${error}`)
    })

    // Scroll to first error
    const { errorMessages } = htmlElements()
    const firstError = errorMessages[0]
    firstError?.setAttribute('role', 'alert')

    if (firstError.scrollIntoView) {
        ;(firstError.previousElementSibling as HTMLElement)?.focus() // Focus on first error input
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
}

export const validationInit = (initOptions: InitOptions): InitOptions => {
    const validatedOptions = initOptionsSchema.safeParse(initOptions)
    if (!validatedOptions.success) {
        console.error(
            'validation failure: ',
            JSON.stringify(initOptions.customer),
        )
        dispatchEvent(CheckoutEvent.FAILURE, {
            status: 'zodError',
            error: validatedOptions.error.issues,
            eventDescription:
                'The checkout initialise was attempted with an invalid schema',
        })
        console.error(
            JSON.stringify({
                message: 'Validation error',
                issues: validatedOptions.error.issues,
            }),
        )
        throw new Error(JSON.stringify(validatedOptions.error.issues))
    }

    return validatedOptions.data
}
